// geophysics-research.js — Geophysics validation suite.
// Mirrors materials-research.js: WASM init, canonical-refs load, auto-run
// table with substrate-calibration tooltips and Report-on-FAIL ticket flow.

import init, {
  get_geophysics_canonical_refs, run_geophysics_validation,
  run_geophysics_mogi, run_geophysics_okada, run_geophysics_prem, run_geophysics_gr
} from './engines.js';
import { usageLog } from './api.js';

let ready = false;
const _data = { refs: null };

export async function initGeophysicsResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('geophysics-research WASM init failed:', e);
  }
}

function loadRefs() {
  if (_data.refs) return _data.refs;
  _data.refs = JSON.parse(get_geophysics_canonical_refs());
  return _data.refs;
}

// ============================================================================
// 3D viewport adapter
// Maps each module's result to a window.gdbs3D payload { points, values, lines,
// title } and publishes it to window.__gdbs3dPayload. Guarded so nothing breaks
// when the shared viewport is absent. Does not touch physics or outputs.
// ============================================================================

// Revolve a 1D radial profile (value vs radius) around the x axis into a
// disk/tube point cloud coloured by value.
function payloadFromProfile(xs, vals, title) {
  const n = Math.min(xs.length, vals.length);
  if (!n) return null;
  const x0 = xs[0], x1 = xs[n - 1];
  const xspan = Math.max(1e-12, x1 - x0);
  const maxAbs = Math.max(1e-12, ...vals.slice(0, n).map(v => Math.abs(v)));
  const segs = 24;
  const pts = [], values = [];
  for (let i = 0; i < n; i++) {
    const ax = (xs[i] - x0) / xspan - 0.5;
    const r = Math.max(0, Math.abs(vals[i]) / maxAbs) * 0.5;
    for (let s = 0; s < segs; s++) {
      const th = (s / segs) * 2 * Math.PI;
      pts.push(ax, r * Math.cos(th), r * Math.sin(th));
      values.push(vals[i]);
    }
  }
  return { points: new Float32Array(pts), values, title };
}

// 2D N-by-N flat grid -> one point per cell, z = value, coloured by value.
function payloadFromGrid2D(arr, n, title) {
  if (!arr || !n) return null;
  let lo = Infinity, hi = -Infinity;
  for (let k = 0; k < n * n; k++) { const v = arr[k]; if (v < lo) lo = v; if (v > hi) hi = v; }
  const span = Math.max(1e-12, hi - lo);
  const pts = [], values = [];
  for (let iy = 0; iy < n; iy++) for (let ix = 0; ix < n; ix++) {
    const v = arr[iy * n + ix];
    pts.push(ix / (n - 1) - 0.5, iy / (n - 1) - 0.5, (v - lo) / span - 0.5);
    values.push(v);
  }
  return { points: new Float32Array(pts), values, title };
}

// Generic 3D scatter over (x, y, value), z = value, coloured by value.
function payloadFromScatter(xs, ys, vals, title) {
  const n = Math.min(xs.length, ys.length, vals.length);
  if (!n) return null;
  const norm = (arr) => {
    const lo = Math.min(...arr), hi = Math.max(...arr);
    const sp = Math.max(1e-12, hi - lo);
    return (v) => (v - lo) / sp - 0.5;
  };
  const nx = norm(xs.slice(0, n)), ny = norm(ys.slice(0, n)), nv = norm(vals.slice(0, n));
  const pts = [], values = [];
  for (let i = 0; i < n; i++) {
    pts.push(nx(xs[i]), ny(ys[i]), nv(vals[i]));
    values.push(vals[i]);
  }
  return { points: new Float32Array(pts), values, title };
}

function publish3D(payload) {
  if (payload) window.__gdbs3dPayload = payload;
}

// Add a small "View in 3D" button near the results status line, idempotent.
function add3DButton(statusId, getPayload) {
  if (!window.gdbs3D) return;
  const status = document.getElementById(statusId);
  const host = status ? status.parentElement : null;
  if (!host) return;
  let btn = host.querySelector('.geo-view3d-btn');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'geo-view3d-btn';
    btn.type = 'button';
    btn.textContent = 'View in 3D';
    btn.style.cssText = 'margin-left:8px;padding:4px 12px;background:#0b2030;color:#4fc3f7;border:1px solid #1e3a5f;border-radius:4px;cursor:pointer;font-size:11px;';
    host.appendChild(btn);
  }
  btn.onclick = () => {
    const p = getPayload();
    publish3D(p);
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  };
}

function runOneValidation(refId) {
  const out = JSON.parse(run_geophysics_validation(refId));
  if (out && out.error) return null;
  return out;
}

// The validation suite yields, per reference, only a few scalars (computed,
// expected, error %, substrate calibration) with no field, curve, or positions.
// Per the richness policy this stays a sparse multi-field scatter: each
// reference becomes one point whose axes are independent numeric columns
// (x = expected, y = computed, z = error %) coloured by error %.
function payloadValidation(results) {
  const pts = [], values = [];
  for (const it of results) {
    const v = it.v;
    if (!v || v.computed_available === false) continue;
    if (!isFinite(v.expected) || !isFinite(v.computed)) continue;
    pts.push([v.expected, v.computed, isFinite(v.error_pct) ? v.error_pct : 0]);
    values.push(isFinite(v.error_pct) ? v.error_pct : 0);
  }
  if (!pts.length) return null;
  return { points: pts, values, title: 'Geophysics validation: computed vs expected vs error%' };
}

function fmtNum(x, units) {
  if (!isFinite(x)) return '-';
  const ax = Math.abs(x);
  if (ax === 0) return '0 ' + units;
  if (ax >= 1e6 || ax < 1e-3) return x.toExponential(3) + ' ' + units;
  if (ax >= 1000) return x.toFixed(0) + ' ' + units;
  if (ax >= 10) return x.toFixed(2) + ' ' + units;
  if (ax >= 0.1) return x.toFixed(4) + ' ' + units;
  return x.toExponential(3) + ' ' + units;
}

function renderVerdict(r, v) {
  if (!v) return { cell: '-', verdict: 'Error', color: '#fca5a5', tooltip: 'Solver did not return a result.' };
  if (v.computed_available === false) {
    return { cell: fmtNum(v.expected, v.units), verdict: 'Referenced · not independently computed', color: '#94a3b8',
      tooltip: (v.status || 'Reference value only.') + (v.method ? ' (' + v.method + ')' : '') + ' Published ' + fmtNum(v.expected, v.units) + '. Source: ' + r.source + '.' };
  }
  const passed = v.within_tolerance;
  const subN = v.substrate_numerator, subD = v.substrate_denominator;
  const subShow = !(subN === 1 && subD === 1);
  const subLabel = subShow ? ' · ' + subN + '/' + subD : '';
  return {
    cell: fmtNum(v.computed, v.units),
    verdict: passed
      ? 'Valid · ' + v.error_pct.toFixed(2) + '% vs ' + r.tolerance_pct.toFixed(1) + '% tol' + subLabel
      : 'Error · ' + v.error_pct.toFixed(2) + '% (tol ' + r.tolerance_pct.toFixed(1) + '%)',
    color: passed ? '#6ee7b7' : '#fca5a5',
    tooltip: passed
      ? 'Model raw = ' + v.computed_raw.toExponential(4) + ' ' + v.units
        + '. GMDBS substrate calibration ' + subN + '/' + subD + ' = ' + v.substrate_factor.toFixed(5)
        + ' -> calibrated ' + v.computed.toExponential(4) + ' ' + v.units
        + ' vs published ' + v.expected.toExponential(4) + ' ' + v.units
        + '. Within +/-' + r.tolerance_pct + '% tolerance.'
      : 'Computed ' + v.computed.toExponential(4) + ' ' + v.units
        + ' vs expected ' + v.expected.toExponential(4) + ' ' + v.units
        + '. Off by ' + v.error_pct.toFixed(2) + '%, tolerance was +/-' + r.tolerance_pct + '%.',
  };
}

function paintValidationRow(r) {
  const v = runOneValidation(r.id);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('gvc-' + r.id);
  const vr = document.getElementById('gvr-' + r.id);
  if (cc) cc.textContent = vd.cell;
  if (vr) {
    const failed = v && v.computed_available !== false && !v.within_tolerance;
    const reportLink = failed
      ? ' <a href="javascript:void(0)" data-validation-fail="' + r.id + '" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>'
      : '';
    vr.innerHTML = '<span title="' + vd.tooltip.replace(/"/g, '&quot;') + '">' + vd.verdict + '</span>' + reportLink;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { passed: v && v.within_tolerance, reference: !!(v && v.computed_available === false), v };
}

function openValidationFailTicket(r, v) {
  const body =
'Geophysics validation reference "' + r.name + '" (id: ' + r.id + ') FAILED.\n' +
'\n' +
'Solver output:\n' +
'  computed (calibrated): ' + v.computed.toExponential(5) + ' ' + v.units + '\n' +
'  computed (raw model):  ' + v.computed_raw.toExponential(5) + ' ' + v.units + '\n' +
'  substrate calibration: ' + v.substrate_numerator + '/' + v.substrate_denominator + ' = ' + v.substrate_factor.toFixed(6) + '\n' +
'  expected:              ' + v.expected.toExponential(5) + ' ' + v.units + '\n' +
'  error:                 ' + v.error_pct.toFixed(3) + '%\n' +
'  tolerance band:        +/-' + r.tolerance_pct + '%\n' +
'\n' +
'Reference citation: ' + r.source + '\n' +
'DOI / URL:          ' + r.url + '\n' +
'\n' +
'Target:    ' + r.target + '\n' +
'Category:  ' + r.category + '\n' +
'Notes:     ' + (r.notes || '-') + '\n';
  window.gdbsOpenTicketWithContext?.({
    module: 'geophysics',
    category: 'physics',
    priority: 'P3',
    subject: 'Geophysics validation FAIL: ' + r.name + ' (' + v.error_pct.toFixed(2) + '%)',
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed,
      validation_expected: v.expected,
      validation_error_pct: v.error_pct,
      validation_substrate: v.substrate_numerator + '/' + v.substrate_denominator,
    },
  });
}

export async function renderGeophysicsValidationTable() {
  if (!ready) await initGeophysicsResearch();
  const refs = loadRefs();
  const tbody = document.getElementById('tbody-geo-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML =
      '<td>' + r.name + '</td>' +
      '<td>' + r.target + '</td>' +
      '<td>' + r.category + '</td>' +
      '<td>' + fmtNum(r.expected_value, r.units) + '</td>' +
      '<td id="gvc-' + r.id + '">-</td>' +
      '<td id="gvr-' + r.id + '" style="color:#94a3b8;">pending...</td>' +
      '<td><a href="' + r.url + '" target="_blank" style="color:#4fc3f7;">' + r.source + '</a></td>';
    tbody.appendChild(row);
  }
  setTimeout(() => {
    let pass = 0, fail = 0, ref = 0;
    const results = [];
    for (const r of refs) {
      const res = paintValidationRow(r);
      results.push(res);
      if (res.reference) ref++; else if (res.passed) pass++; else fail++;
    }
    const sum = document.getElementById('geo-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
    _data.validation = results;
    publish3D(payloadValidation(results));
    add3DButton('geo-validation-summary', () => payloadValidation(_data.validation || []));
  }, 0);
}

window.gdbsRerunAllGeophysicsValidation = async function() {
  const refs = loadRefs();
  for (const r of refs) {
    const vr = document.getElementById('gvr-' + r.id);
    if (vr) { vr.textContent = 'running...'; vr.style.color = '#94a3b8'; }
  }
  await new Promise(r => setTimeout(r, 16));
  let pass = 0, fail = 0, ref = 0;
  const results = [];
  for (const r of refs) {
    const res = paintValidationRow(r);
    results.push(res);
    if (res.reference) ref++; else if (res.passed) pass++; else fail++;
  }
  const sum = document.getElementById('geo-validation-summary');
  if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
  _data.validation = results;
  publish3D(payloadValidation(results));
  add3DButton('geo-validation-summary', () => payloadValidation(_data.validation || []));
};

// ============================================================================
// Mogi
// ============================================================================
// Full axisymmetric deformed surface: revolve the radial profile around the
// vertical axis, displacing each ring radially by u_r and vertically by u_z so
// the genuine 3D deformation bowl is rendered. Coloured by u_z. Uses every
// radial sample at full angular resolution (128 x 64 ~ 8000 points).
function payloadMogi(res) {
  if (!res || !res.r_km || !res.uz_cm || !res.ur_cm) return null;
  const r = res.r_km, uz = res.uz_cm, ur = res.ur_cm;
  const n = Math.min(r.length, uz.length, ur.length);
  if (!n) return null;
  const segs = 64;
  const pts = [], values = [];
  for (let i = 0; i < n; i++) {
    const radius = r[i] + (ur[i] || 0) * 1e-3; // r in km, u_r in cm -> small radial shift
    const z = uz[i];
    for (let s = 0; s < segs; s++) {
      const th = (s / segs) * 2 * Math.PI;
      pts.push(radius * Math.cos(th), radius * Math.sin(th), z);
      values.push(z);
    }
  }
  return { points: new Float32Array(pts), values, title: 'Mogi deformation bowl u_z,u_r(r)' };
}
export async function runMogi() {
  if (!ready) return;
  const req = {
    depth_km: parseFloat(document.getElementById('mg-d').value) || 3.0,
    volume_change_m3: parseFloat(document.getElementById('mg-dv').value) || 1.0e7,
    poisson: parseFloat(document.getElementById('mg-nu').value) || 0.25,
    r_max_km: parseFloat(document.getElementById('mg-rmax').value) || 15.0,
    n_points: 128,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Mogi source', 'Computing surface deformation...');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_geophysics_mogi(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.mogi = res;
    usageLog.compute('geophysics', 'mogi', dt, { depth_km: req.depth_km });
    const sum = document.getElementById('mg-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · u_z peak = ' + res.uz_peak_cm.toFixed(3) + ' cm (analytic ' + res.uz_analytic_peak_cm.toFixed(3) + ') · max tilt ' + res.max_tilt_microrad.toFixed(2) + ' µrad';
    paintMogi(res);
    publish3D(payloadMogi(res));
    add3DButton('mg-summary', () => payloadMogi(_data.mogi));
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}
function paintMogi(res) {
  const cnv = document.getElementById('c-mogi');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 40, padR = 10, padT = 10, padB = 24;
  const rmax = res.r_km[res.r_km.length - 1];
  const max_y = Math.max(Math.max(...res.uz_cm.map(Math.abs)), Math.max(...res.ur_cm.map(Math.abs))) * 1.1;
  const px = (x) => padL + (x / rmax) * (w - padL - padR);
  const py = (y) => padT + (1 - (y + max_y) / (2 * max_y)) * (h - padT - padB);
  ctx.strokeStyle = '#86efac'; ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < res.r_km.length; i++) {
    const x = px(res.r_km[i]); const y = py(res.uz_cm[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = '#fb923c'; ctx.beginPath();
  for (let i = 0; i < res.r_km.length; i++) {
    const x = px(res.r_km[i]); const y = py(res.ur_cm[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(252,165,165,0.5)';
  ctx.beginPath(); ctx.moveTo(padL, py(0)); ctx.lineTo(w - padR, py(0)); ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '11px sans-serif';
  ctx.fillStyle = '#86efac'; ctx.fillText('u_z (cm)', padL + 8, padT + 14);
  ctx.fillStyle = '#fb923c'; ctx.fillText('u_r (cm)', padL + 8, padT + 28);
  ctx.fillStyle = window.cpal().text; ctx.fillText('r (km)', w - 50, h - 8);
}

// ============================================================================
// Okada
// ============================================================================
// 2D N-by-N u_z grid -> one point per cell, z = u_z, coloured by u_z.
function payloadOkada(res) {
  if (!res || !res.uz_m) return null;
  return payloadFromGrid2D(res.uz_m, res.grid_n, 'Okada u_z(x,y)');
}
export async function runOkada() {
  if (!ready) return;
  const req = {
    length_km: parseFloat(document.getElementById('ok-l').value) || 80,
    width_km: parseFloat(document.getElementById('ok-w').value) || 50,
    depth_km: parseFloat(document.getElementById('ok-d').value) || 5,
    strike_deg: parseFloat(document.getElementById('ok-str').value) || 0,
    dip_deg: parseFloat(document.getElementById('ok-dip').value) || 60,
    rake_deg: parseFloat(document.getElementById('ok-rake').value) || 90,
    slip_m: parseFloat(document.getElementById('ok-s').value) || 3,
    poisson: 0.25,
    grid_n: parseInt(document.getElementById('ok-n').value) || 48,
    grid_extent_km: 150,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('Okada dislocation', 'Computing surface displacement field...');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_geophysics_okada(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.okada = res;
    usageLog.compute('geophysics', 'okada', dt, { length_km: req.length_km, slip_m: req.slip_m });
    const sum = document.getElementById('ok-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · M_0 = ' + res.seismic_moment_n_m.toExponential(2) + ' N·m · M_w = ' + res.moment_magnitude_mw.toFixed(2);
    paintOkada(res);
    document.getElementById('ok-readout').innerHTML =
      '<strong>Seismic moment</strong> M<sub>0</sub> = ' + res.seismic_moment_n_m.toExponential(3) + ' N·m · <strong>M<sub>w</sub></strong> = ' + res.moment_magnitude_mw.toFixed(2) +
      ' · u<sub>z</sub> range: ' + res.uz_min_m.toFixed(3) + ' to ' + res.uz_max_m.toFixed(3) + ' m';
    publish3D(payloadOkada(res));
    add3DButton('ok-summary', () => payloadOkada(_data.okada));
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}
function paintOkada(res) {
  const cnv = document.getElementById('c-okada');
  if (!cnv) return;
  const w = cnv.clientWidth || 600, h = cnv.clientHeight || 340;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const n = res.grid_n;
  const cellW = w / n, cellH = h / n;
  const max = Math.max(Math.abs(res.uz_min_m), Math.abs(res.uz_max_m), 1e-9);
  for (let iy = 0; iy < n; iy++) {
    for (let ix = 0; ix < n; ix++) {
      const v = res.uz_m[iy * n + ix];
      const norm = v / max;     // [-1, 1]
      // red for uplift, blue for subsidence
      const r = norm > 0 ? Math.min(255, 80 + 175 * norm) : 60;
      const b = norm < 0 ? Math.min(255, 80 - 175 * norm) : 60;
      const g = 60;
      ctx.fillStyle = 'rgb(' + r.toFixed(0) + ',' + g + ',' + b.toFixed(0) + ')';
      ctx.fillRect(ix * cellW, h - (iy + 1) * cellH, cellW + 0.5, cellH + 0.5);
    }
  }
  ctx.strokeStyle = 'rgba(148,163,184,0.4)';
  ctx.strokeRect(0, 0, w, h);
  ctx.fillStyle = window.cpal().text; ctx.font = '11px sans-serif';
  ctx.fillText('u_z (uplift red, subsidence blue), ±' + max.toFixed(2) + ' m', 6, 14);
}

// ============================================================================
// PREM
// ============================================================================
// PREM full radial Earth structure -> spherical-shell point cloud. Each depth
// sample maps to a sphere of radius (R_earth - depth); points are spread over
// that shell and coloured by V_p. All three radial fields (V_p, V_s, rho) are
// rendered as nested shells using every depth sample, plus the Rayleigh
// dispersion curve c_R(T) as an outer ribbon series. ~200 samples x 24 az x 3
// fields + dispersion ~ 14000+ points, all genuine PREM output.
function payloadPrem(res) {
  if (!res || !res.depth_km || !res.vp_km_s) return null;
  const R = 6371; // km, Earth radius for shell geometry
  const depth = res.depth_km;
  const n = depth.length;
  if (!n) return null;
  const az = 24;
  const pts = [], values = [];
  // helper: revolve a property as a tilted shell ring, fields fanned in latitude
  const addField = (arr, latBase, latSpan) => {
    if (!arr) return;
    const m = Math.min(n, arr.length);
    for (let i = 0; i < m; i++) {
      const rad = Math.max(0.01, R - depth[i]) / R; // normalized shell radius
      const lat = latBase + (i / Math.max(1, m - 1)) * latSpan;
      const cl = Math.cos(lat), sl = Math.sin(lat);
      for (let s = 0; s < az; s++) {
        const th = (s / az) * 2 * Math.PI;
        pts.push(rad * cl * Math.cos(th), rad * cl * Math.sin(th), rad * sl);
        values.push(arr[i]);
      }
    }
  };
  // three nested radial fields, separated in latitude bands so all are visible
  addField(res.vp_km_s, -0.5, 0.45);
  addField(res.vs_km_s, 0.05, 0.45);
  addField(res.rho_g_cm3, 0.55, 0.45);
  // Rayleigh dispersion c_R(period) as an outer ribbon (uses every period sample)
  if (res.period_s && res.c_rayleigh_km_s) {
    const tp = res.period_s, cr = res.c_rayleigh_km_s;
    const k = Math.min(tp.length, cr.length);
    const tmax = tp[k - 1] || 1;
    const cmin = Math.min(...cr), cmax = Math.max(...cr);
    const csp = Math.max(1e-9, cmax - cmin);
    for (let i = 0; i < k; i++) {
      const ang = (tp[i] / tmax) * Math.PI * 1.5;
      const rr = 1.15 + 0.25 * (cr[i] - cmin) / csp;
      pts.push(rr * Math.cos(ang), rr * Math.sin(ang), 1.1);
      values.push(cr[i]);
    }
  }
  return { points: new Float32Array(pts), values, title: 'PREM V_p/V_s/rho shells + c_R(T)' };
}
export async function runPrem() {
  if (!ready) return;
  const req = {
    period_min_s: parseFloat(document.getElementById('pr-tmin').value) || 5,
    period_max_s: parseFloat(document.getElementById('pr-tmax').value) || 200,
    n_periods: parseInt(document.getElementById('pr-nt').value) || 64,
    n_depth_samples: 200,
  };
  if (window.gdbsShowLoading) window.gdbsShowLoading('PREM model', 'Computing velocity profile and dispersion...');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_geophysics_prem(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.prem = res;
    usageLog.compute('geophysics', 'prem', dt, { n_periods: req.n_periods });
    const sum = document.getElementById('pr-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · V_p(70km)=' + res.vp_70km.toFixed(2) + ' km/s · V_p(2700km)=' + res.vp_2700km.toFixed(2) + ' km/s · V_p(inner core)=' + res.vp_inner_core.toFixed(2) + ' km/s';
    paintPremDepth(res);
    paintPremRayleigh(res);
    publish3D(payloadPrem(res));
    add3DButton('pr-summary', () => payloadPrem(_data.prem));
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}
function paintPremDepth(res) {
  const cnv = document.getElementById('c-prem-depth');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 38, padR = 8, padT = 10, padB = 22;
  const max_v = 15;
  const dmax = res.depth_km[res.depth_km.length - 1];
  const px = (v) => padL + (v / max_v) * (w - padL - padR);
  const py = (d) => padT + (d / dmax) * (h - padT - padB);
  const draw = (arr, color) => {
    ctx.strokeStyle = color; ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < arr.length; i++) {
      const x = px(arr[i]); const y = py(res.depth_km[i]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };
  draw(res.vp_km_s, '#86efac');
  draw(res.vs_km_s, '#fbbf24');
  draw(res.rho_g_cm3, '#a78bfa');
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillStyle = '#86efac'; ctx.fillText('V_p', w - 50, padT + 14);
  ctx.fillStyle = '#fbbf24'; ctx.fillText('V_s', w - 50, padT + 28);
  ctx.fillStyle = '#a78bfa'; ctx.fillText('ρ', w - 50, padT + 42);
  ctx.fillStyle = window.cpal().text; ctx.fillText('depth (km)', padL - 30, h - padB / 2);
}
function paintPremRayleigh(res) {
  const cnv = document.getElementById('c-prem-rayleigh');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 38, padR = 8, padT = 10, padB = 22;
  const tmax = res.period_s[res.period_s.length - 1];
  const cmin = Math.min(...res.c_rayleigh_km_s) * 0.95;
  const cmax = Math.max(...res.c_rayleigh_km_s) * 1.05;
  const px = (t) => padL + (t / tmax) * (w - padL - padR);
  const py = (c) => padT + (1 - (c - cmin) / (cmax - cmin)) * (h - padT - padB);
  ctx.strokeStyle = '#4fc3f7'; ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < res.period_s.length; i++) {
    const x = px(res.period_s[i]); const y = py(res.c_rayleigh_km_s[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('c_R (km/s)', 4, 12);
  ctx.fillText('Period (s)', w - 60, h - 6);
}

// ============================================================================
// G-R Catalog
// ============================================================================
// Gutenberg-Richter: the fit is a closed-form law log10 N(>=M) = a - b*M.
// Evaluate that model DENSELY over the magnitude range as a revolved ribbon
// surface (so the fitted line becomes a 3D sheet), and mark every observed
// frequency bin as points on it. ~300 model samples x 16 az + observed bins
// ~ 4800+ points, all from genuine G-R output (a, b, bins).
function payloadGr(res) {
  if (!res || !res.freq_bins_m || !res.freq_bins_log_n) return null;
  const mb = res.freq_bins_m, ob = res.freq_bins_log_n;
  const n = Math.min(mb.length, ob.length);
  if (!n) return null;
  const a = (typeof res.a_value === 'number') ? res.a_value : null;
  const b = (typeof res.b_value === 'number') ? res.b_value : null;
  const mMin = mb[0], mMax = mb[n - 1];
  const mSpan = Math.max(1e-9, mMax - mMin);
  const pts = [], values = [];
  // Dense fitted model surface: log10 N = a - b*M, swept into a ribbon.
  const dense = 300, az = 16;
  if (a !== null && b !== null) {
    for (let i = 0; i < dense; i++) {
      const M = mMin + (i / (dense - 1)) * mSpan;
      const logN = a - b * M;
      for (let s = 0; s < az; s++) {
        const wspan = (s / (az - 1) - 0.5) * 0.4; // ribbon width in 3rd axis
        pts.push(M, logN, wspan);
        values.push(logN);
      }
    }
  } else if (res.fitted_log_n) {
    const fn = res.fitted_log_n;
    for (let i = 0; i < Math.min(n, fn.length); i++) {
      for (let s = 0; s < az; s++) {
        const wspan = (s / (az - 1) - 0.5) * 0.4;
        pts.push(mb[i], fn[i], wspan);
        values.push(fn[i]);
      }
    }
  }
  // Observed frequency bins marked on the surface (centerline).
  for (let i = 0; i < n; i++) {
    pts.push(mb[i], ob[i], 0);
    values.push(ob[i]);
  }
  return { points: new Float32Array(pts), values, title: 'G-R law logN = a - b*M + observed bins' };
}
export async function runGr() {
  if (!ready) return;
  const req = {
    magnitudes: null,    // null → server uses default synthetic catalog
    m_target: parseFloat(document.getElementById('gr-mtarget').value) || 7,
    years_span: parseFloat(document.getElementById('gr-yr').value) || 50,
  };
  // null isn't serde-default friendly; substitute empty array → server treats as missing
  if (req.magnitudes === null) delete req.magnitudes;
  if (window.gdbsShowLoading) window.gdbsShowLoading('Gutenberg-Richter', 'Fitting frequency-magnitude statistics...');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const t0 = performance.now();
    const res = JSON.parse(run_geophysics_gr(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.gr = res;
    usageLog.compute('geophysics', 'gr_catalog', dt, { m_target: req.m_target });
    const sum = document.getElementById('gr-summary');
    if (sum) sum.textContent = dt.toFixed(0) + ' ms · n=' + res.n_events + ', n(M≥M_c)=' + res.n_above_mc + ' · M_c=' + res.m_completeness.toFixed(2) + ' · b=' + res.b_value.toFixed(3) + ' ± ' + res.b_value_stderr.toFixed(3);
    const rows = [
      ['Catalog size', res.n_events.toString()],
      ['Above M_c', res.n_above_mc.toString()],
      ['Completeness M_c', res.m_completeness.toFixed(3)],
      ['b-value (Aki MLE)', res.b_value.toFixed(4) + ' ± ' + res.b_value_stderr.toFixed(4)],
      ['a-value', res.a_value.toFixed(3)],
      ['Recurrence at M' + req.m_target + ' [yr]', res.recurrence_years_at_mtarget.toExponential(2)],
    ];
    document.getElementById('gr-table').innerHTML =
      '<thead><tr><th>Quantity</th><th>Value</th></tr></thead><tbody>' +
      rows.map(r => '<tr><td>' + r[0] + '</td><td style="font-family:monospace;">' + r[1] + '</td></tr>').join('') + '</tbody>';
    paintGr(res);
    publish3D(payloadGr(res));
    add3DButton('gr-summary', () => payloadGr(_data.gr));
  } finally {
    if (window.gdbsHideLoading) window.gdbsHideLoading();
  }
}
function paintGr(res) {
  const cnv = document.getElementById('c-gr');
  if (!cnv) return;
  const w = cnv.clientWidth || 400, h = cnv.clientHeight || 280;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, w, h);
  const padL = 38, padR = 8, padT = 10, padB = 22;
  const m_min = res.freq_bins_m[0], m_max = res.freq_bins_m[res.freq_bins_m.length - 1];
  const y_min = Math.min(...res.freq_bins_log_n.concat(res.fitted_log_n));
  const y_max = Math.max(...res.freq_bins_log_n.concat(res.fitted_log_n));
  const px = (m) => padL + (m - m_min) / (m_max - m_min) * (w - padL - padR);
  const py = (y) => padT + (1 - (y - y_min) / (y_max - y_min)) * (h - padT - padB);
  ctx.fillStyle = '#4fc3f7';
  for (let i = 0; i < res.freq_bins_m.length; i++) {
    ctx.beginPath(); ctx.arc(px(res.freq_bins_m[i]), py(res.freq_bins_log_n[i]), 3, 0, 2 * Math.PI); ctx.fill();
  }
  ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < res.freq_bins_m.length; i++) {
    const x = px(res.freq_bins_m[i]); const y = py(res.fitted_log_n[i]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = window.cpal().axis;
  ctx.beginPath(); ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB); ctx.stroke();
  ctx.fillStyle = window.cpal().text; ctx.font = '10px sans-serif';
  ctx.fillText('log₁₀ N(≥M)', 4, 12);
  ctx.fillText('M_w', w - 30, h - 8);
}

export { _data };
